import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { useEffect, useRef } from 'react';
import { useStateInRef } from 'seagull';
import { useContacts } from '~/hooks/reactQuery/queries/useContacts';
import { handlePromise } from '~/infra/utils';
import { NewContactModel } from '~/models/ContactModel';
import { useStoreActions } from '~/store/store-context';
import { isCompleteReviewer } from '../utils/isCompleteReviewer';
const reviewerEmailKey = (reviewer)=>{
    var _reviewer_email;
    return ((_reviewer_email = reviewer.email) !== null && _reviewer_email !== void 0 ? _reviewer_email : '').toLowerCase();
};
export const useMigrateLegacyDraftRecipients = (param)=>{
    let { primaryContact, primaryContactId, reviewers, contactsLoaded, refreshExistingContacts, onReviewersChange } = param;
    const { contacts } = useContacts();
    const { api } = useStoreActions();
    const orphanClearedRef = useRef(false);
    const partialsDroppedRef = useRef(false);
    const everHadPrimaryRef = useRef(false);
    const migrationAttemptedRef = useRef(new Set());
    const argsRef = useStateInRef({
        primaryContact,
        primaryContactId,
        reviewers,
        contactsLoaded,
        refreshExistingContacts,
        onReviewersChange,
        contacts
    });
    useEffect(()=>{
        const { primaryContact: primary, primaryContactId: primaryId, reviewers: revs, contactsLoaded: loaded, onReviewersChange: setReviewers, contacts: list } = argsRef();
        if (!loaded) return;
        if (primary == null) {
            if (primaryId != null) return;
            if (everHadPrimaryRef.current) return;
            if (orphanClearedRef.current) return;
            if (revs.length > 0) {
                orphanClearedRef.current = true;
                setReviewers([]);
            }
            return;
        }
        everHadPrimaryRef.current = true;
        if (!partialsDroppedRef.current) {
            partialsDroppedRef.current = true;
            const hasPartial = revs.some((r)=>!isCompleteReviewer(r));
            if (hasPartial) {
                setReviewers(revs.filter(isCompleteReviewer));
                return;
            }
        }
        if (revs.length === 0) return;
        const contactEmails = new Set(list.map((c)=>c.email().toLowerCase()));
        const candidates = revs.filter((r)=>{
            if (r.clientContactId != null) return false;
            const key = reviewerEmailKey(r);
            if (key.length === 0) return false;
            if (contactEmails.has(key)) return false;
            if (migrationAttemptedRef.current.has(key)) return false;
            return true;
        });
        if (candidates.length === 0) return;
        candidates.forEach((r)=>migrationAttemptedRef.current.add(reviewerEmailKey(r)));
        const companyName = primary.companyName();
        Promise.all(candidates.map(async (r)=>{
            var _r_firstName, _r_lastName, _r_email;
            const created = await handlePromise(api.contact.create(NewContactModel.make({
                firstName: (_r_firstName = r.firstName) !== null && _r_firstName !== void 0 ? _r_firstName : '',
                lastName: (_r_lastName = r.lastName) !== null && _r_lastName !== void 0 ? _r_lastName : '',
                email: (_r_email = r.email) !== null && _r_email !== void 0 ? _r_email : '',
                companyName,
                phone: null
            })));
            return {
                reviewer: r,
                created
            };
        })).then(async (results)=>{
            const reviewersByEmail = new Map();
            results.forEach((param)=>{
                let { reviewer, created } = param;
                if (created.success && reviewer.email != null) {
                    reviewersByEmail.set(reviewerEmailKey(reviewer), created.data.id);
                } else {
                    migrationAttemptedRef.current.delete(reviewerEmailKey(reviewer));
                }
            });
            await argsRef().refreshExistingContacts();
            const latestReviewers = argsRef().reviewers;
            const updated = latestReviewers.map((r)=>{
                if (r.clientContactId != null) return r;
                const mappedId = reviewersByEmail.get(reviewerEmailKey(r));
                return mappedId != null ? _object_spread_props(_object_spread({}, r), {
                    clientContactId: mappedId
                }) : r;
            });
            if (updated.some((r, i)=>{
                var _latestReviewers_i;
                return r.clientContactId !== ((_latestReviewers_i = latestReviewers[i]) === null || _latestReviewers_i === void 0 ? void 0 : _latestReviewers_i.clientContactId);
            })) {
                argsRef().onReviewersChange(updated);
            }
        });
    }, [
        primaryContact,
        primaryContactId,
        reviewers.length,
        contactsLoaded,
        contacts.length
    ]);
};
